import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts } from '../contexts/ProductContext';
import ProductCard from '../components/product/ProductCard';
import { getDisplayCategory } from '../types/product';
import {
  extractCategoriesFromVariants,
  extractGalwayCategories,
  getRawCategory,
} from '../utils/categoryUtils';
import { getMappedCategory, getUnifiedCategories } from '../utils/categoryMapping';
import { COLLECTIONS } from '../types/collection';
import './ShopPage.css';

const ShopPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    allProducts, 
    allProductVariants, 
    trophiesProducts,
    trophiesVariants,
    galwayProducts,
    loading, 
    searchTerm, 
    setSearchTerm 
  } = useProducts();
  const categoryFromUrl = searchParams.get('category') || 'All';
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);

  // Update category from URL when it changes
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category') || 'All';
    setSelectedCategory(categoryFromUrl);
  }, [searchParams]);

  // Extract categories from both collections and combine them
  const categories = useMemo(() => {
    const trophiesCategories = extractCategoriesFromVariants(trophiesVariants);
    const galwayCategories = extractGalwayCategories(galwayProducts);
    return getUnifiedCategories(trophiesCategories, galwayCategories);
  }, [trophiesVariants, galwayProducts]);

  // Filter Galway Crystal products
  const filteredGalwayProducts = useMemo(() => {
    let filtered = galwayProducts;

    // Filter by category (using mapped categories)
    if (selectedCategory !== 'All') {
      filtered = filtered.filter((p) => {
        const mappedCategory = getMappedCategory(p.category, p.type);
        const mappedType = getMappedCategory(p.type, undefined);
        // Check if the product's category or type maps to the selected category
        return mappedCategory === selectedCategory || 
               mappedType === selectedCategory;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.category?.toLowerCase().includes(term) ||
          (p.sku && p.sku.toLowerCase().includes(term)) ||
          (p.type && p.type.toLowerCase().includes(term))
      );
    }

    return filtered;
  }, [galwayProducts, selectedCategory, searchTerm]);

  // Filter Trophies & Medals variants
  const filteredTrophiesVariants = useMemo(() => {
    let filtered = trophiesVariants;

    // Filter by category
    if (selectedCategory !== 'All') {
      const rawCategory = getRawCategory(selectedCategory);
      filtered = filtered.filter((v) =>
        getDisplayCategory(v.defaultVariant).toLowerCase() === rawCategory.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((v) => {
        // Search in variant's base properties
        if (
          v.name.toLowerCase().includes(term) ||
          v.description?.toLowerCase().includes(term) ||
          v.category?.toLowerCase().includes(term)
        ) {
          return true;
        }

        // Search in any of the variant products
        return v.variants.some(
          (p) =>
            p.name.toLowerCase().includes(term) ||
            p.productId.toLowerCase().includes(term) ||
            (p.sku && p.sku.toLowerCase().includes(term))
        );
      });
    }

    return filtered;
  }, [trophiesVariants, selectedCategory, searchTerm]);

  if (loading) {
    return (
      <div className="shop-page-loading">
        <div className="spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="shop-page">
      {/* Category Filter */}
      <div className="category-filter">
        {categories.map((category) => (
          <button
            key={category}
            className={`category-chip ${
              selectedCategory === category ? 'category-chip-active' : ''
            }`}
            onClick={() => {
              setSelectedCategory(category);
              if (category === 'All') {
                searchParams.delete('category');
              } else {
                searchParams.set('category', category);
              }
              setSearchParams(searchParams);
            }}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Product Grid - Combined from both collections */}
      {filteredTrophiesVariants.length === 0 && filteredGalwayProducts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏆</div>
          <p>No products found</p>
        </div>
      ) : (
        <div className="product-grid">
          {/* Trophies & Medals variants */}
          {filteredTrophiesVariants.map((variant) => (
            <ProductCard key={variant.baseId} variant={variant} />
          ))}
          {/* Galway Crystal products - use productId as key to avoid duplicates */}
          {filteredGalwayProducts.map((product) => (
            <ProductCard key={`galway-${product.id || product.productId || product.sku}`} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopPage;

